import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularLoading } from 'respinner';
import { z } from 'zod';

import { MochiInput } from '@autopass-web/mochi-component';
import { CoreIcon } from '@autopass-web/mochi-icons';
import { barcodeCarrierContentOnlyStrategy } from '@autopass-web/mochi-utils';

import { useImperativeForm } from '../../../hooks/use-bind-async-submit';
import { emailSchema } from '../utils';
import { EmailInput } from './UI';
import styles from './ui.module.scss';

import type { ReceiptInfoBase } from '../../../configs/receipts';
import type { EmailFieldConfig, ReceiptCompProps } from '../utils';

export interface CarrierMobileReceiptInfo extends ReceiptInfoBase {
  type: 'carrier_mobile_barcode';
  barcode: string;
  email: string;
}

type CarrierMobileReceiptForm = Omit<CarrierMobileReceiptInfo, 'type'>;

export const SuccessCheckHint = () => {
  return (
    <div className={styles['check-hint-container']}>
      <CoreIcon
        className={styles['check-icon']}
        src="/assets/check.svg"
        style={{
          height: 16,
        }}
      />
      已驗證
    </div>
  );
};

type CarrierMobileReceiptProps = ReceiptCompProps<{
  defaultValues?: CarrierMobileReceiptForm;
  validateBarcode?: (barcode: string) => Promise<boolean>;
  emailFieldConfig: EmailFieldConfig;
}>;

export const CarrierMobileReceipt = ({
  controlRef,
  validateBarcode,
  emailFieldConfig,
  storage,
  formMode,
}: CarrierMobileReceiptProps) => {
  const [isValidatingBarcode, setIsValidatingBarcode] =
    useState<boolean>(false);

  const schema =
    emailFieldConfig === 'required'
      ? z.object({ email: emailSchema, barcode: barcodeSchema })
      : emailFieldConfig === 'default'
        ? z.object({
            email: emailSchema.or(z.string().length(0)),
            barcode: barcodeSchema,
          })
        : z.object({
            barcode: barcodeSchema,
          });

  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    getFieldState,
    subscribe,
    setValue,
    setError,
  } = useForm<CarrierMobileReceiptForm>({
    defaultValues: { ...(storage?.get?.() ?? {}) },
    resolver: zodResolver(schema),
    mode: formMode ?? 'onSubmit',
  });

  const barcodeFieldState = getFieldState('barcode');

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const receiptInfo = storage?.get?.();

        storage?.set({
          ...receiptInfo,
          ...values,
          type: 'carrier_mobile_barcode',
        });
      },
    });

    return () => cb();
  }, [subscribe]);

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      name: 'barcode',
      callback: async ({ values }) => {
        const { barcode } = values;

        if (barcode.length >= 7 && validateBarcode) {
          setIsValidatingBarcode(true);
          const isValid = await validateBarcode(`/${barcode}`);

          if (!isValid) {
            setError('barcode', { message: '手機載具條碼錯誤' });
            trigger('barcode');
          }

          setIsValidatingBarcode(false);
        }
      },
    });
    return () => cb();
  }, [subscribe]);

  const formProps = useImperativeForm(
    controlRef,
    errors,
    (submit) => {
      return handleSubmit((data) => {
        submit({
          ...data,
          type: 'carrier_mobile_barcode',
          barcode: '/' + data.barcode,
        });
      });
    },
    'carrier_mobile_barcode-mobile',
  );

  return (
    <form {...formProps}>
      <div className={styles['barcode-input']}>
        <MochiInput
          beforeValue="/"
          variant="grey"
          inputMode="text"
          placeholder="輸入 7 位數手機條碼載具"
          maxLength={7}
          errorMessage={errors?.barcode?.message}
          {...register('barcode', {
            onBlur() {
              trigger('barcode');
            },
            async onChange(e) {
              const value = e.target.value as string;
              setValue('barcode', value.toUpperCase());
              trigger('barcode');
            },
          })}
        />
        {!!validateBarcode &&
          !isValidatingBarcode &&
          barcodeFieldState.isTouched &&
          !barcodeFieldState.invalid && <SuccessCheckHint />}
        {!!validateBarcode && isValidatingBarcode && (
          <CircularLoading
            className={styles['spinner']}
            size={16}
            duration={1}
          />
        )}
      </div>
      <EmailInput
        config={emailFieldConfig}
        errorMessage={errors.email?.message}
        {...register('email')}
      />
    </form>
  );
};

const barcodeSchema = z
  .string()
  .min(1, {
    message: '此欄位為必填',
  })
  .refine(barcodeCarrierContentOnlyStrategy, {
    message: '輸入格式錯誤，需為 / 後 7 位英文、數字或符號組成',
  });
